"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import {
  API_BASE,
  api,
  eventSocket,
  latestTraining,
  type Alert,
  type Device,
  type Frame,
  type Snapshot,
  type TrainingSession,
} from "@/lib/peared-api"

const primary =
  "rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
const secondary = "rounded-full border border-border px-5 py-2.5 text-sm font-semibold disabled:opacity-40"
const input = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
const activeStatuses = new Set(["ready", "collecting", "awaiting_next", "training"])
const official = [
  { id: "hunger", label: "Hunger", urgency: "Low urgency" },
  { id: "pain", label: "Pain", urgency: "Urgent" },
  { id: "toilet", label: "Toilet", urgency: "Fairly urgent" },
  { id: "help", label: "Help", urgency: "Fairly urgent" },
]

export function TrainingExperience() {
  const [tokenInput, setTokenInput] = useState("")
  const [token, setToken] = useState("")
  const [devices, setDevices] = useState<Device[]>([])
  const [clientId, setClientId] = useState("")
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [connection, setConnection] = useState("Disconnected")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<"train" | "monitor">("train")
  const [mesh, setMesh] = useState(true)
  const [target, setTarget] = useState(120)
  const [newId, setNewId] = useState("pi-01")
  const [newKind, setNewKind] = useState<"camera" | "buzzer">("camera")
  const [newRoom, setNewRoom] = useState("")
  const [deviceToken, setDeviceToken] = useState<{ id: string; token: string } | null>(null)
  const [now, setNow] = useState(Date.now())
  const socketRef = useRef<WebSocket | null>(null)

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true)
    setError("")
    try {
      await action()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Request failed")
    } finally {
      setBusy(false)
    }
  }, [])

  const refreshDevices = useCallback(async (secret: string) => {
    const result = await api<{ clients: Device[] }>(secret, "/clients")
    setDevices(result.clients)
    return result.clients
  }, [])

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!token || !clientId) return
    let disposed = false
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let pollInFlight = false
    let socket: WebSocket | null = null
    setSnapshot(null)
    const applySnapshot = (value: Snapshot) => {
      setSnapshot((previous) => ({
        ...value,
        training: latestTraining(previous?.training ?? null, value.training),
        frame:
          previous?.frame && value.frame && previous.frame.received_ms > value.frame.received_ms
            ? previous.frame
            : value.frame,
      }))
    }
    const connect = () => {
      if (disposed) return
      setConnection("Connecting…")
      socket = eventSocket(clientId)
      socketRef.current = socket
      socket.onopen = () => socket?.send(JSON.stringify({ type: "auth", token }))
      socket.onmessage = (event) => {
        if (disposed) return
        try {
          const message = JSON.parse(event.data)
          if (message.type === "snapshot") {
            applySnapshot(message.data)
            setConnection("Live")
          } else if (message.type === "frame")
            setSnapshot((s) => (s ? { ...s, frame: message.data as Frame } : s))
          else if (message.type === "training")
            setSnapshot((s) =>
              s ? { ...s, training: latestTraining(s.training, message.data as TrainingSession) } : s,
            )
          else if (message.type === "client")
            setSnapshot((s) => (s ? { ...s, client: message.data as Device } : s))
          else if (message.type === "alert")
            setSnapshot((s) =>
              s
                ? {
                    ...s,
                    alerts: [
                      message.data as Alert,
                      ...s.alerts.filter((a) => a.alert_id !== message.data.alert_id),
                    ].slice(0, 100),
                  }
                : s,
            )
        } catch {
          setError("Could not read a server update")
        }
      }
      socket.onclose = (event) => {
        if (disposed) return
        setConnection(event.code === 1008 ? "Connection refused" : "Reconnecting…")
        if (event.code !== 1008) reconnectTimer = setTimeout(connect, 2000)
      }
      socket.onerror = () => {
        if (!disposed) setConnection("Using HTTP fallback")
      }
    }
    connect()
    const poll = async () => {
      if (disposed || pollInFlight) return
      pollInFlight = true
      try {
        const [clients, health] = await Promise.all([
          api<{ clients: Device[] }>(token, "/clients"),
          fetch(`${API_BASE}/health`, { cache: "no-store", signal: AbortSignal.timeout(5000) }).then((r) =>
            r.json(),
          ),
        ])
        if (disposed) return
        setDevices(clients.clients)
        const device = clients.clients.find((d) => d.client_id === clientId)
        setSnapshot((s) =>
          s && device ? { ...s, client: device, mqtt_connected: Boolean(health.mqtt_connected) } : s,
        )
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          const value = await api<Snapshot>(token, `/clients/${clientId}/snapshot`)
          if (!disposed) applySnapshot(value)
        }
      } catch (cause) {
        if (!disposed) setError(cause instanceof Error ? cause.message : "Server unreachable")
      } finally {
        pollInFlight = false
      }
    }
    void poll()
    const pollTimer = setInterval(() => void poll(), 3000)
    return () => {
      disposed = true
      clearInterval(pollTimer)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      socket?.close()
      socketRef.current = null
    }
  }, [token, clientId])

  const client = snapshot?.client
  const frame = snapshot?.frame
  const session = snapshot?.training
  const step = session?.steps[session.step_index]
  const trainingActive = Boolean(session && activeStatuses.has(session.status))
  const cameras = devices.filter((d) => d.kind === "camera")
  const buzzers = devices.filter((d) => d.kind === "buzzer")
  const live = Boolean(frame && now - frame.received_ms < 5000)
  const prediction = frame?.prediction

  const patchClient = (body: object) =>
    run(async () => {
      const updated = await api<Device>(token, `/clients/${clientId}`, "PATCH", body)
      setSnapshot((s) => (s ? { ...s, client: updated } : s))
      await refreshDevices(token)
    })
  const sessionAction = (action: string) =>
    run(async () => {
      if (!session) return
      const updated = await api<TrainingSession>(
        token,
        `/training/sessions/${session.session_id}/${action}`,
        "POST",
      )
      setSnapshot((s) => (s ? { ...s, training: latestTraining(s.training, updated) } : s))
    })

  if (!token)
    return (
      <form
        className="max-w-lg rounded-3xl border border-border bg-card p-8"
        onSubmit={(event) => {
          event.preventDefault()
          void run(async () => {
            const clients = await refreshDevices(tokenInput.trim())
            setToken(tokenInput.trim())
            setTokenInput("")
            setClientId(clients.find((d) => d.kind === "camera")?.client_id ?? "")
          })
        }}
      >
        <h2 className="font-display text-2xl font-bold">Connect to Peared</h2>
        <p className="mb-5 mt-2 text-sm text-muted-foreground">
          Enter the operator token configured on your server. It stays in this page’s memory.
        </p>
        <label className="text-sm font-medium" htmlFor="operator-token">
          Operator token
        </label>
        <input
          id="operator-token"
          type="password"
          autoComplete="off"
          className={`${input} mt-2`}
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          required
        />
        <button className={`${primary} mt-4`} disabled={busy}>
          {busy ? "Connecting…" : "Connect"}
        </button>
        {error && (
          <p role="alert" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
      </form>
    )

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4">
        <label className="flex items-center gap-3 text-sm font-semibold">
          Camera
          <select
            className={input}
            disabled={busy}
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value)
              setError("")
            }}
          >
            {!cameras.length && <option value="">Register a camera below</option>}
            {cameras.map((d) => (
              <option value={d.client_id} key={d.client_id}>
                {d.display_name} {d.room && `· ${d.room}`}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs text-muted-foreground">
          {connection} · MQTT {snapshot?.mqtt_connected ? "connected" : "disconnected"}
        </span>
        <button
          className={secondary}
          onClick={() => {
            socketRef.current?.close()
            setToken("")
            setSnapshot(null)
            setDeviceToken(null)
          }}
        >
          Disconnect
        </button>
      </div>
      {error && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </p>
      )}
      {clientId && (
        <div className="grid items-start gap-8 lg:grid-cols-[1.3fr_1fr]">
          <div className="space-y-4 lg:sticky lg:top-28">
            <div className="relative aspect-video overflow-hidden rounded-3xl border border-border bg-muted">
              {frame ? (
                <img
                  alt={`Raspberry Pi camera ${clientId}`}
                  src={`data:image/jpeg;base64,${frame.image.data}`}
                  className={`h-full w-full object-contain ${live ? "" : "opacity-40"}`}
                />
              ) : (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
                  Waiting for the Raspberry Pi camera. Start its client using the registered device token.
                </div>
              )}
              {live && mesh && !frame?.image.annotated && prediction?.landmarks.length ? (
                <svg
                  className="pointer-events-none absolute inset-0 h-full w-full"
                  viewBox={`0 0 ${frame?.image.width} ${frame?.image.height}`}
                  preserveAspectRatio="xMidYMid meet"
                  aria-label="Face mesh landmarks"
                >
                  {prediction.landmarks.map((p, i) => (
                    <circle
                      key={i}
                      cx={p.x * (frame?.image.width ?? 640)}
                      cy={p.y * (frame?.image.height ?? 480)}
                      r={1.2}
                      fill="#22c55e"
                    />
                  ))}
                </svg>
              ) : null}
              <span
                className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${live ? "bg-primary text-primary-foreground" : "bg-background text-foreground"}`}
              >
                {live ? "Pi camera · Live" : "No recent frame"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 px-1 text-xs text-muted-foreground">
              <span>Frames stream through your server. Only the latest frame is kept in memory.</span>
              <label className="flex shrink-0 items-center gap-2">
                <input type="checkbox" checked={mesh} onChange={(e) => setMesh(e.target.checked)} /> Face mesh
              </label>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5" aria-live="polite">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Latest prediction</p>
              <p className="mt-1 font-display text-2xl font-bold">
                {live
                  ? prediction?.face_present
                    ? prediction.class_name === "neutral"
                      ? "No gesture"
                      : (official.find((c) => c.id === prediction.class_name)?.label ?? "Model not trained")
                    : "No face detected"
                  : "Camera offline"}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {prediction?.confidence != null && live
                  ? `${Math.round(prediction.confidence * 100)}% confidence`
                  : "Waiting for a prediction"}
                {prediction?.head_pose && live
                  ? ` · yaw ${prediction.head_pose.yaw.toFixed(0)}° · pitch ${prediction.head_pose.pitch.toFixed(0)}° · roll ${prediction.head_pose.roll.toFixed(0)}°`
                  : ""}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {official.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border p-4">
                  <p className="font-semibold">{c.label}</p>
                  <p className={`text-xs ${c.id === "pain" ? "text-red-700" : "text-muted-foreground"}`}>
                    {c.urgency}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-1 rounded-full border border-border bg-muted p-1">
              {(["train", "monitor"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  aria-pressed={mode === m}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                >
                  {m === "train" ? "Guided training" : "Monitor & alerts"}
                </button>
              ))}
            </div>
            {mode === "train" ? (
              <div className="rounded-3xl border border-border bg-card p-6">
                <h2 className="font-display text-xl font-bold">Learn your gestures</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Choose a distinct facial gesture for each request. Keep the same gesture while gently moving
                  your head. Training happens on the Pi; progress counts real face samples.
                </p>
                {!trainingActive && (
                  <div className="mt-5 space-y-4">
                    <label className="flex items-center justify-between gap-3 text-sm">
                      Samples per class
                      <select
                        className="rounded-lg border border-border bg-background p-2"
                        value={target}
                        onChange={(e) => setTarget(Number(e.target.value))}
                      >
                        {[60, 120, 240].map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className={primary}
                      disabled={busy || !client?.online}
                      onClick={() =>
                        void run(async () => {
                          const training = await api<TrainingSession>(token, "/training/sessions", "POST", {
                            client_id: clientId,
                            target_samples: target,
                            include_neutral: true,
                          })
                          setSnapshot((s) =>
                            s ? { ...s, training: latestTraining(s.training, training) } : s,
                          )
                        })
                      }
                    >
                      {session?.status === "completed" ? "Train a new model" : "Create training session"}
                    </button>
                  </div>
                )}
                {session && (
                  <div className="mt-6 space-y-4">
                    {session.steps.map((item, index) => (
                      <div
                        key={item.id}
                        className={`rounded-xl border p-3 ${index === session.step_index && trainingActive ? "border-primary bg-primary/5" : "border-border"}`}
                      >
                        <div className="flex justify-between text-sm">
                          <span>{item.label}</span>
                          <span>
                            {item.collected_samples}/{session.target_samples}
                          </span>
                        </div>
                        <progress
                          className="mt-2 h-2 w-full accent-green-700"
                          max={session.target_samples}
                          value={item.collected_samples}
                          aria-label={`${item.label} samples`}
                        />
                      </div>
                    ))}
                    {trainingActive && step && (
                      <p className="text-sm leading-relaxed">
                        {session.status === "training"
                          ? "All samples collected. The Raspberry Pi is training and saving the model…"
                          : step.instruction}
                      </p>
                    )}
                    {session.status === "collecting" && (
                      <p className="text-sm text-muted-foreground">
                        Collecting {step?.label}…{" "}
                        {live && !prediction?.face_present
                          ? "Bring your face into view to continue."
                          : "Keep making this gesture until collection finishes."}
                      </p>
                    )}
                    {session.status === "awaiting_next" && (
                      <p role="status" className="text-sm font-semibold">
                        {step?.label} collection is finished.{" "}
                        {session.step_index + 1 < session.steps.length
                          ? "Ready for the next class?"
                          : "Ready to train your model?"}
                      </p>
                    )}
                    {(session.status === "ready" || session.status === "awaiting_next") && (
                      <button
                        className={primary}
                        disabled={busy || !client?.online}
                        onClick={() => void sessionAction("advance")}
                      >
                        {session.status === "ready"
                          ? `Begin ${step?.label}`
                          : session.step_index + 1 < session.steps.length
                            ? `Begin ${session.steps[session.step_index + 1].label}`
                            : "Train model on the Pi"}
                      </button>
                    )}
                    {trainingActive && (
                      <button
                        className={`${secondary} ml-2`}
                        disabled={busy}
                        onClick={() => void sessionAction("cancel")}
                      >
                        Cancel session
                      </button>
                    )}
                    {session.status === "completed" && (
                      <p role="status" className="text-sm font-semibold text-green-800">
                        Model saved on the Raspberry Pi. Open Monitor &amp; alerts to try your gestures.
                      </p>
                    )}
                    {session.status === "failed" && (
                      <p role="alert" className="text-sm text-red-700">
                        Training failed: {session.error}
                      </p>
                    )}
                    {session.status === "cancelled" && (
                      <p className="text-sm text-muted-foreground">This session was cancelled.</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-3xl border border-border bg-card p-6">
                <h2 className="font-display text-xl font-bold">Nurse alerts</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  A confirmed gesture sends its class, urgency and room to the assigned receivers. Alerts
                  pause during training.
                </p>
                <fieldset className="mt-5 space-y-2">
                  <legend className="mb-2 text-sm font-semibold">Assigned receivers</legend>
                  {buzzers.length ? (
                    buzzers.map((b) => (
                      <label className="flex items-center gap-3 text-sm" key={b.client_id}>
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={client?.buzzer_ids.includes(b.client_id) ?? false}
                          onChange={(e) =>
                            void patchClient({
                              buzzer_ids: e.target.checked
                                ? [...(client?.buzzer_ids ?? []), b.client_id]
                                : client?.buzzer_ids.filter((id) => id !== b.client_id),
                              ...(!e.target.checked && client?.buzzer_ids.length === 1
                                ? { alerts_enabled: false }
                                : {}),
                            })
                          }
                        />
                        {b.display_name}
                        <span className="text-xs text-muted-foreground">
                          {b.online ? "Online" : "Offline"}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Register a buzzer below to route alerts.</p>
                  )}
                </fieldset>
                <label className="mt-5 flex items-center gap-3 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={client?.alerts_enabled ?? false}
                    disabled={busy || !client?.buzzer_ids.length}
                    onChange={(e) => void patchClient({ alerts_enabled: e.target.checked })}
                  />
                  Enable gesture alerts
                </label>
                <p className="mt-2 text-xs text-muted-foreground">
                  {trainingActive
                    ? "Paused while this training session is active."
                    : client?.alerts_enabled
                      ? "Confirmed gestures will create real alerts."
                      : "Monitoring only. Enable alerts when ready."}
                </p>
                <div className="mt-6 space-y-3">
                  {snapshot?.alerts.length ? (
                    snapshot.alerts.slice(0, 10).map((alert) => {
                      const received = Object.values(alert.deliveries).some((d) => d.received_ms)
                      const published = Object.values(alert.deliveries).some((d) => d.broker_accepted_ms)
                      return (
                        <div
                          className={`rounded-xl border p-4 ${alert.class_name === "pain" && alert.status === "open" ? "border-red-300 bg-red-50 text-red-900" : "border-border"}`}
                          key={alert.alert_id}
                        >
                          <div className="flex justify-between gap-3">
                            <span className="font-semibold">
                              {alert.label} {alert.room && `· ${alert.room}`}
                            </span>
                            <span className="text-xs capitalize">{alert.urgency.replaceAll("_", " ")}</span>
                          </div>
                          <p className="mt-1 text-xs">
                            {new Date(alert.created_ms).toLocaleTimeString()} ·{" "}
                            {alert.status === "open"
                              ? received
                                ? "Receiver confirmed receipt"
                                : published
                                  ? "Broker accepted; awaiting receiver"
                                  : "Waiting for MQTT delivery"
                              : alert.status}
                          </p>
                          {alert.status === "open" && (
                            <button
                              className={`${secondary} mt-3`}
                              disabled={busy}
                              onClick={() =>
                                void run(async () => {
                                  const updated = await api<Alert>(
                                    token,
                                    `/alerts/${alert.alert_id}/acknowledge`,
                                    "POST",
                                  )
                                  setSnapshot((s) =>
                                    s
                                      ? {
                                          ...s,
                                          alerts: s.alerts.map((a) =>
                                            a.alert_id === updated.alert_id ? updated : a,
                                          ),
                                        }
                                      : s,
                                  )
                                })
                              }
                            >
                              Mark acknowledged
                            </button>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground">No alerts yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <details className="rounded-2xl border border-border bg-card p-5" open={!cameras.length}>
        <summary className="cursor-pointer font-semibold">Register a Raspberry Pi or nurse receiver</summary>
        <form
          className="mt-5 grid gap-4 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault()
            void run(async () => {
              const result = await api<{ client: Device; token: string }>(token, "/clients", "POST", {
                client_id: newId,
                kind: newKind,
                display_name: newId,
                room: newRoom,
              })
              setDeviceToken({ id: newId, token: result.token })
              await refreshDevices(token)
              if (result.client.kind === "camera") setClientId(newId)
            })
          }}
        >
          <label className="space-y-2 text-sm">
            Device ID
            <input
              className={input}
              required
              pattern={"[A-Za-z0-9][A-Za-z0-9_\\-]{0,63}"}
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
            />
          </label>
          <label className="space-y-2 text-sm">
            Type
            <select
              className={input}
              value={newKind}
              onChange={(e) => {
                const kind = e.target.value as "camera" | "buzzer"
                setNewKind(kind)
                setNewId(kind === "camera" ? "pi-01" : "nurse-01")
              }}
            >
              <option value="camera">Raspberry Pi camera</option>
              <option value="buzzer">ESP32 nurse receiver</option>
            </select>
          </label>
          <label className="space-y-2 text-sm">
            Room or bed
            <input
              className={input}
              value={newRoom}
              maxLength={40}
              onChange={(e) => setNewRoom(e.target.value)}
            />
          </label>
          <button className={`${primary} justify-self-start`} disabled={busy}>
            Register device
          </button>
        </form>
      </details>
      {deviceToken && (
        <div className="rounded-xl bg-muted p-4">
          <p className="text-sm font-semibold">{deviceToken.id} registered — save its device token now</p>
          <input
            aria-label="New device token"
            className={`${input} mt-2 font-mono text-xs`}
            readOnly
            value={deviceToken.token}
            onFocus={(e) => e.target.select()}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Camera: use this as SERVER_TOKEN. The ESP32 uses separate MQTT broker credentials; this token is
            for its optional HTTP API.
          </p>
          <button className={`${secondary} mt-3`} onClick={() => setDeviceToken(null)}>
            Hide token
          </button>
        </div>
      )}
    </div>
  )
}
